/**
 * 获取联系人详情
 * @author fredhaochen
 */

import { createContainer } from 'unstated-next'
import { useQuery, useService, useShowError } from '@hooks'
import { useLocation, useParams } from 'react-router-dom'
import { useCallback, useMemo, useState } from 'react'
import { callCustomer, getCustomerInfo, UpdateCustomer, getCustomerPhoneNumber, createPstn, dismissSalesMeeting, getBindingRecords } from '@services/customer'
import { isArray, isEmptyWithCustom, isEqualArray, isNullOrUndefined, parseCustomerExtend, getStaffName } from '@library'
import { useContactInfoService } from '@pages/customer/detail/hooks/useContactInfoService'
import { CallCustomerPayload, CustomerStatusEnum, LeadOrCustomer, UpdateCustomerFilter } from '@types'
import { message } from 'antd'
import { useTask } from '@pages/customer/detail/hooks/useTask'
import { useFollowList } from '@pages/customer/detail/hooks/useFollowList'
import { useSaleOpportunity } from '@pages/customer/detail/hooks/useSaleOpportunity'
import { globalStore, sessionStore, t3cStore } from '@store'
import { getNextCustomerId } from '@pages/customer/detail/utils'
import { moduleId } from '@configs'
import { uniq } from 'lodash'
import { ModuleNameMapping } from '@configs/constants'
import { useSdrFollowList } from './useSdrFollowList'
import { useConditionalService } from '@hooks/useConditionalService'

function useCustomerInfoDetail () {
  const userInfo = sessionStore(s => s.userInfo)
  const { t3cReady, t3cErrorMsg, t3cfailed, reinitT3cSdk, curTabUUID, setCallInfo, t3cSdkReady, t3cSdkReadyTime, t3cSdkUrl } = t3cStore()
  const authPointMap = globalStore(s => s.authPointMap)
  /* 基本信息 */
  const customerId = +useParams<{ customerId: string }>()?.customerId

  /* 客户信息 */
  const [customerInfoLoading, customerInfo, infoErr, forceRequestCustomerInfo] = useService(getCustomerInfo, customerId)
  useShowError('获取客户信息失败', infoErr)
  const customerExtendInfo = useMemo(() => parseCustomerExtend(customerInfo?.extend), [customerInfo])
  const cid = useMemo(() => customerInfo?.cid, [customerInfo])

  // 是否客户只读，业务上意味着客户转出了
  const readonly = useMemo(() => customerInfo?.status === CustomerStatusEnum.TRANFSFER_OUT, [customerInfo]) // 转出客户池的客户只读

  // 是否是跟进人
  const isFollower = useMemo(() => customerInfo?.follower === userInfo?.salesName, [customerInfo, userInfo])

  // 是否可以提交跟进记录
  const canFollow = useMemo(() => {
    // 是否显示跟进记录表单，和权限，是否是跟进人，是否是转出客户三个因素相关
    if (readonly) return false
    if (authPointMap.addFollowRecord?.result) return true
    return isFollower
  }, [readonly, isFollower, authPointMap])

  // 是否可以提交市场部SDR跟进记录
  const canFollowSdr = useMemo(() => {
    // 是否显示跟进记录表单，和权限，是否是跟进人两个因素相关
    if (authPointMap.addFollowRecordSdrPermission?.result) return true
    return isFollower
  }, [isFollower, authPointMap])

  /* 客户联系人信息 */
  const [contactLoading, contactInfoList, recommendedContact, forceRequestContactInfoList] = useContactInfoService(cid)

  /* 任务和线索信息 */
  const [
    taskLoading,
    taskTotal,
    listUnhandled,
    listHandled,
    listClosed,
    /* 任务分类 */
    selectedTaskIds, // 已勾选的任务ids
    setSelectedTaskIds, // 设置勾选的任务
    updateLeadTag,
    forceRequestTask,
  ] = useTask(cid)

  /* 跟进记录 */
  const [followListLoading, followList, pagination, followListFilter, setFollowListFilter, forceRequestFollowList, rowKey] = useFollowList(customerId)
  const [sdrFollowListLoading, sdrFollowList, sdrPagination, sdrFollowListFilter, setSdrFollowListFilter, forceRequestSdrFollowList, sdrRowKey] = useSdrFollowList(customerId)

  /* 打电话和发短信 */
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [phoneLogIds, setPhoneLogIds] = useState<string[]>([]) // 就是CallRecordListItem.callId
  const [msgLogIds, setMsgLogIds] = useState<string[]>([]) // 不是SMSRecordListItem.id
  const [isCalling, setIsCalling] = useState(false)
  const [isMeetingCalling, setIsMeetingCalling] = useState(false)
  const handleClickPhone = useCallback(async (param: LeadOrCustomer, phone: string, extraParam?: { skillGroupId?: string; callerPhoneNumber?: string }) => {
    if (isCalling) {
      return
    }
    setIsCalling(true)
    setTimeout(() => {
      setIsCalling(false)
    }, 1500)
    const [, dismissErr] = await dismissSalesMeeting()
    if (dismissErr) {
      message.error(`结束当前会议失败(${dismissErr.message})`)
      return
    }
    // 仅coc两个模块需要customerID参数
    const params: CallCustomerPayload = [moduleId.COC_TURN_ID, moduleId.COC_STUDY_ID].includes(userInfo.belongModule) ? { ...param, customerId } : param
    if (userInfo.useT3C) {
      // 邮箱验证：检查用户是否绑定邮箱
      const hasEmail = userInfo.email && userInfo.email.trim() !== ''
      if (!hasEmail) {
        message.warning('当前账号未绑定邮箱,无法接入 TCCC 服务,如需外呼服务请联系业务管理员')
        return
      }
      if (!t3cSdkReady) {
        message.error(`tccc SDK脚本加载中，请稍后再试。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
        return
      }
      if (!t3cReady) {
        message.error(`tccc未就绪,请稍后再试。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}; 脚本加载时间: ${t3cSdkReadyTime}; t3cSdkUrl: ${t3cSdkUrl}`)
        return
      }
      if (t3cfailed) {
        message.error(`T3C 失败: ${t3cErrorMsg}。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}; 脚本加载时间: ${t3cSdkReadyTime}; t3cSdkUrl: ${t3cSdkUrl}`)
        return
      }
      console.log('当前坐席状态:', window.tccc?.Agent?.getStatus())
      if (window.tccc?.Agent?.getStatus() === 'busy') {
        message.error(`正在呼叫中，请稍后再试。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}; 脚本加载时间: ${t3cSdkReadyTime}; t3cSdkUrl: ${t3cSdkUrl}`)
        return
      }
      if (window.tccc?.Agent?.getStatus() === 'offline' && t3cReady) {
        reinitT3cSdk({ refresh: true })
        message.success(`当前坐席已在其他地方登录，正在重新登录，请稍后再试。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}; 脚本加载时间: ${t3cSdkReadyTime}; t3cSdkUrl: ${t3cSdkUrl}`)
        console.log('当前坐席已在其他地方登录，正在重新登录，请稍后再试')
        // message.error('已在 T3C（https://tccc.qcloud.com/ ）登录，请在 T3C 退出登录后，刷新当前页面重新尝试')
        return
      }
      const [res, err] = await getCustomerPhoneNumber(params)
      if (err) {
        message.error(err.message)
        return
      }
      try {
        const { skillGroupId, callerPhoneNumber } = extraParam || {}
        const extraCallParam = callerPhoneNumber ? {
          callerPhoneNumber,
          skillGroupId,
        } : {}
        console.log('呼叫参数:', {
          phoneNumber: res?.phone,
          uui: JSON.stringify({ recordId: res?.recordId?.toString() }),
          ...extraCallParam,
        })

        window.XSY_TRACK?.trackClick('tccc_call', {
          content: {
            belong_module: userInfo?.belongModule,
            belong_module_name: ModuleNameMapping[userInfo?.belongModule],
            follower: getStaffName(userInfo.salesName, userInfo.salesNameCh),
            tccc_call_tabUUID: window?.tccc?.uuid,
            tccc_call_recordId: res?.recordId?.toString(),
            tccc_call_phone: res?.phone,
            tccc_call_from: 'customer_detail',
          },
        })
        await window.tccc?.Call?.startOutboundCall({
          phoneNumber: res?.phone,
          uui: JSON.stringify({ recordId: res?.recordId?.toString() }),
          ...extraCallParam,
        })
        setCallInfo(info => {
          return {
            ...info,
            calleeName: customerInfo?.name,
            leadId: param?.leadId || undefined,
            cid,
            recordId: res?.recordId,
          }
        })
        setPhoneLogIds(phoneLogIds => phoneLogIds.concat(res?.recordId?.toString()))
        // 如果是根据线索打的电话，打完电话要自动勾选上任务，以供提交跟进表单时上送
        if (param?.leadId) {
          const taskId = [...listUnhandled, ...listHandled].find(item => item?.leadInfo?.leadId === param.leadId)?.taskId
          if (taskId) setSelectedTaskIds(taskIds => uniq(taskIds.concat(taskId)))
        }
      } catch (e) {
        message.error(`T3C呼叫客户失败(${e?.message})。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
        console.log('T3C呼叫客户失败', e)
        // message
      }
    } else {
      const [res, err] = await callCustomer(params)
      if (err) {
        message.error(`呼叫客户失败(${err.message})`)
        return
      }
      message.success('已呼叫客户，请在企点客户端查看')
      setPhoneLogIds(phoneLogIds => phoneLogIds.concat(res?.logId))
      // 如果是根据线索打的电话，打完电话要自动勾选上任务，以供提交跟进表单时上送
      if (param?.leadId) {
        const taskId = [...listUnhandled, ...listHandled].find(item => item?.leadInfo?.leadId === param.leadId)?.taskId
        if (taskId) setSelectedTaskIds(taskIds => uniq(taskIds.concat(taskId)))
      }
    }
  }, [isCalling, t3cReady, setSelectedTaskIds, listUnhandled, listHandled, setPhoneLogIds, userInfo, customerId])

  const handleClickMeeting = useCallback(async (param: LeadOrCustomer, callParam: { phone?: string, cid?: string }) => {
    if (isMeetingCalling) {
      return
    }
    setIsMeetingCalling(true)
    setTimeout(() => {
      setIsMeetingCalling(false)
    }, 3000)
    const [, dismissErr] = await dismissSalesMeeting()
    if (dismissErr) {
      message.error(`结束当前会议失败(${dismissErr.message})`)
      return
    }
    message.success('创建会议中，请勿重复点击并确保企微已打开')
    // 这里的参数和打电话一样，customerId,contactId,leadId三选一
    const params: CallCustomerPayload = [moduleId.COC_TURN_ID, moduleId.COC_STUDY_ID].includes(userInfo.belongModule) ? { ...param, customerId } : param
    const [, pstnErr] = await createPstn({ cid: callParam.cid, ...params })
    if (pstnErr) {
      message.error(`${pstnErr.message}`)
      return
    }
    message.success('会议已创建，请点击会议浮窗入会')
  }, [isMeetingCalling])

  const [leadOrCustomer, setLeadOrCustomer] = useState<LeadOrCustomer>(undefined) // 发短信时可以根据线索或者联系方式进行发短信
  const openMsgModal = useCallback(async (param: LeadOrCustomer) => {
    setLeadOrCustomer(param)
    setShowMessageModal(true)
  }, [setLeadOrCustomer, setShowMessageModal])
  const handleMsgSent = useCallback((logId: string) => {
    setMsgLogIds(msgLogIds => msgLogIds.concat(logId))
    if (leadOrCustomer?.leadId) {
      // 如果是根据线索打的电话，打完电话要自动勾选上线索
      if (leadOrCustomer?.leadId) {
        const taskId = [...listUnhandled, ...listHandled].find(item => item?.leadInfo?.leadId === leadOrCustomer.leadId)?.taskId
        if (taskId) setSelectedTaskIds(taskIds => uniq(taskIds.concat(taskId)))
      }
    }
    setShowMessageModal(false)
  }, [setMsgLogIds, setShowMessageModal, leadOrCustomer])

  // 是否可以拨打电话或发短信等联系客户
  const canContactCustomer = useMemo(() => {
    /**
     * 以下权限不好控制，交给后端控制。 前端只要控制是否转出一条即可
     *
     * 客户必须是未转出。或者以下条件满足一个即有权限：
       有全量客户权限点tcc.permission.allCustomer-r），可以联系客户
       客户当前跟进人，可以联系客户
       客户当前跟进人的上级，即公司负责人或群组负责人，可以联系客户
       有虚拟账号权限点（tcc.permission.company.account.customer-r），则可以联系跟进人为对应公司公海池的客户
     */
    return !readonly
  }, [readonly])

  /* 其它 */
  // 提交修改标签
  const updateCustomerTag = useCallback(async (tags: number[], type: 'public' | 'private') => {
    if (!isArray(tags)) return
    // 如果tags相比原来没有变化，则不提交
    if (isEqualArray(tags, customerInfo?.[`${type}Tags`]?.map?.(tag => tag[`${type}TagId`]))) return

    const payload: UpdateCustomerFilter = {
      filter: { cid: cid && [cid] },
      updateResult: {
        [`updateCustomer${type.charAt(0).toUpperCase() + type.slice(1)}Tags`]: {
          [`${type}TagIds`]: tags,
        },
      },
    }
    const [, err] = await UpdateCustomer(payload)
    if (err) {
      message.error(`打标失败${err.message}`)
      return
    }
    message.success('打标成功')
    forceRequestCustomerInfo()
  }, [cid, customerInfo, forceRequestCustomerInfo])

  // 提交修改IT规模
  const updateCustomerItScale = useCallback(async (itScale: number) => {
    const payload: UpdateCustomerFilter = {
      filter: { cid: cid && [cid] },
      updateResult: { itScale },
    }
    const [, err] = await UpdateCustomer(payload)
    if (err) {
      message.error(`更新IT规模失败${err.message}`)
      return
    }
    message.success('更新IT规模成功')
    forceRequestCustomerInfo()
  }, [cid, customerInfo, forceRequestCustomerInfo])

  /* 下一个客户 */
  const { search } = useLocation()
  const { queue } = useQuery<{ queue: number }>()
  const showNext = useMemo(() => !isEmptyWithCustom(queue), [queue])

  const gotoNextCustomer = async () => {
    const nextId = await getNextCustomerId(queue, customerId)
    if (isNullOrUndefined(nextId)) {
      await message.info('已跟进完所勾选或所筛选的客户')
    } else {
      message.success('加载下一个客户...')
      window.open(`/customer/${nextId}${search}`, '_self')
    }
  }

  /* 客户销售机会 */
  const [
    saleOpportunityLoading,
    saleOpportunityList,
    saleOpportunityTotal,
    saleOpportunityProcessingTotal,
    saleOpportunityReload,
    addSaleOpportunity,
    updateSaleOpportunity,
  ] = useSaleOpportunity(cid)
  const saleOpportunityCanComment = authPointMap.commentOpportunity?.result
  const saleOpportunityCanEdit = useMemo(() => {
    if (readonly) return false
    if (authPointMap.addFollowRecord?.result) return true
    return isFollower
  }, [authPointMap, isFollower, readonly])
  const bindingRecordsParams = useMemo(() => {
    return { cid: [cid] }
  }, [cid])
  const [bindingRecordsLoading, bindingRecordsList, bindingRecordsError] = useConditionalService(getBindingRecords, !!cid, bindingRecordsParams)

  return {
    /* 基本信息 */
    cid,
    status: customerInfo?.status,
    customerId,
    readonly,
    isFollower,
    canFollow,
    canFollowSdr,
    /* 客户信息 */
    customer: { customerInfoLoading, customerInfo, customerExtendInfo, forceRequestCustomerInfo },
    /* 客户联系人信息 */
    contact: { contactLoading, contactInfoList, recommendedContact, forceRequestContactInfoList },
    /* 线索相关 */
    task: {
      taskLoading,
      taskTotal,
      listUnhandled,
      listHandled,
      listClosed,
      /* 任务分类 */
      selectedTaskIds, // 已勾选的任务ids
      setSelectedTaskIds, // 设置勾选的任务
      updateLeadTag,
      forceRequestTask,
    },
    /* 跟进记录 */
    follow: { followListLoading, followList, pagination, followListFilter, setFollowListFilter, forceRequestFollowList, rowKey },
    /* 市场部SDR跟进记录 */
    sdrFollow: { sdrFollowListLoading, sdrFollowList, sdrPagination, sdrFollowListFilter, setSdrFollowListFilter, forceRequestSdrFollowList, sdrRowKey },
    /* 打电话发短信 */
    callAndMsg: {
      phoneLogIds,
      msgLogIds,
      setPhoneLogIds,
      setMsgLogIds,
      showMessageModal,
      setShowMessageModal,
      leadOrCustomer,
      handleClickPhone,
      handleClickMeeting,
      openMsgModal,
      handleMsgSent,
      canContactCustomer,
    },
    /* 销售机会 */
    saleOpportunity: {
      saleOpportunityLoading,
      saleOpportunityList,
      saleOpportunityTotal,
      saleOpportunityProcessingTotal,
      saleOpportunityReload,
      addSaleOpportunity,
      updateSaleOpportunity,
      saleOpportunityCanComment,
      saleOpportunityCanEdit,
    },
    /* 其它 */
    updateCustomerTag,
    updateCustomerItScale,
    // forceRequest,
    showNext, // 是否展示下一个按钮
    gotoNextCustomer, // 到下一个客户
    bindingRecords: {
      bindingRecordsLoading,
      bindingRecordsList: bindingRecordsList?.list || [],
      bindingRecordsError,
    },
  }
}

const DetailContainer = createContainer(useCustomerInfoDetail)
export default DetailContainer
